/**
 * The addresses of a running service, for its Apps page (ServiceAddresses): from the preview-runner's status of its
 * session (`info`: `previewUrl`, `kind`, `protocol`, `ports`, `composeEntry`; container-live/preview-runner
 * proxy_targets.rs). Besides its preview, a service may have other named ports (`ports` in `.flow/service.json`), each
 * at `…/port/<name>/proxy/`, and a Docker Compose app other services that publish a port, each at
 * `…/compose/<service>/proxy/`. A `tcp` port has no web address: the project's other services reach it. A worker has
 * no port at all.
 */

/** The service's base address: its preview URL without the trailing `/proxy/` (null without one). */
export function addressBase(previewUrl) {
  if (!previewUrl) { return null; }
  const match = String(previewUrl).match(/^(.*)\/proxy\/?(\?.*)?$/);
  return match ? match[1] : null;
}

const PROTOCOL_LABELS = { http: 'HTTP', grpc: 'gRPC', tcp: 'TCP' };
export const protocolLabel = (protocol) => PROTOCOL_LABELS[protocol] || 'HTTP';

/**
 * `{ worker, main, ports: [{ name, label, protocol, url, hostPort }], compose: [{ service, port, url }] }`: what the card
 * lists. `url` is null where a browser cannot open it (tcp).
 */
export function serviceAddresses(info) {
  const base = addressBase(info?.previewUrl);
  const worker = info?.kind === 'worker';
  const protocol = info?.protocol || 'http';
  const main = worker ? null : { protocol, url: protocol === 'tcp' || !info?.previewUrl ? null : info.previewUrl };
  const ports = (Array.isArray(info?.ports) ? info.ports : []).map((port) => ({
    name: port.name,
    label: `${port.name} · ${protocolLabel(port.protocol)}`,
    protocol: port.protocol || 'http',
    hostPort: port.hostPort || null,
    url: port.protocol === 'tcp' || !base ? null : `${base}/port/${encodeURIComponent(port.name)}/proxy/`,
  }));
  const entry = info?.composeEntry;
  const chosen = entry?.chosen?.service || entry?.candidates?.find((c) => c.chosen)?.service || null;
  const seen = new Set();
  const compose = [];
  for (const candidate of entry?.candidates || []) {
    if (!candidate.hostPort || candidate.service === chosen || seen.has(candidate.service) || candidate.http === false) { continue; }
    seen.add(candidate.service);
    compose.push({ service: candidate.service, port: candidate.port, url: base ? `${base}/compose/${encodeURIComponent(candidate.service)}/proxy/` : null });
  }
  return { worker, main, ports, compose };
}

/** Whether the card has anything to say beyond the preview itself. */
export const hasOtherAddresses = (addresses) => addresses.worker || addresses.ports.length > 0 || addresses.compose.length > 0
  || (addresses.main && addresses.main.protocol !== 'http');
